import { useEffect, useMemo, useState } from "react";

import { getDefaultOptions } from "shared/components/Flights/FlightOffer";

import FlightCombination from "../types/flightCombination";
import FlightOffer from "../types/flightOffer";

/**
 * return selected parts of the flight - [brand, first flight option, second FO,...]
 * e.g. ["CLASSIC", "FO01", "FO02"]
 */

interface IReturn {
  selectedCombination: FlightCombination;
  selectedStreamParts: string[];
  changeSelectedStreamParts: (streamIndex: 0 | 1, newKey: string) => void;
}

const filterAvailableCombinations = (flightOffer, combinations) => {
  const flightStreamKeys: Set<string> = new Set(
    flightOffer?.FlightItinerary?.FlightStream?.flatMap((sublist) =>
      sublist.map((flight) => flight.Key)
    )
  );

  const filteredCombinations = {};
  Object.keys(combinations).forEach((flightClass) => {
    filteredCombinations[flightClass] = combinations[flightClass].filter(
      (combination) => {
        const combinationKeys = combination.map((item) => item.Key);
        return combinationKeys.every((itemKey) =>
          flightStreamKeys.has(itemKey)
        );
      }
    );
  });

  return filteredCombinations;
};

export function useSelectedStreamParts(flightOffer: FlightOffer): IReturn {
  const combinations = flightOffer.CurrentCombinations;

  const memoizedTotalFlightSegmentsCount = useMemo(() => {
    function countFlightSegmentsInItinerary(itinerary) {
      return itinerary.reduce((count, flight) => {
        if (flight.FlightSegments && flight.FlightSegments.FlightSegment) {
          return count + flight.FlightSegments.FlightSegment.length;
        }
        return count;
      }, 0);
    }

    // Function to sum the flight segments in all itineraries
    function sumFlightSegmentsInItineraries(flightData) {
      return flightData.reduce((totalSegments, itinerary) => {
        return totalSegments + countFlightSegmentsInItinerary(itinerary);
      }, 0);
    }

    // Calculate the total number of flight segments
    const totalFlightSegments = sumFlightSegmentsInItineraries(
      flightOffer?.FlightItinerary?.FlightStream
    );

    return totalFlightSegments;
  }, [flightOffer?.FlightItinerary?.FlightStream]);

  useEffect(() => {
    setSelectedPath(
      getDefaultOptions(filterAvailableCombinations(flightOffer, combinations))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedTotalFlightSegmentsCount]);

  const [selectedStreamParts, setSelectedPath] = useState(() =>
    getDefaultOptions(combinations)
  );

  /**
   *
   * @param streamIndex - stream index (1 = forward, 2 = backward)
   * @param newKey - brandName
   * @param isBrandName - using this parameter to efficiently changing default brand options
   */
  function changeSelectedStreamParts(
    streamIndex: 0 | 1,
    newKey: string,
    isBrandName?: false
  ) {
    setSelectedPath((currentSelectedStreamIndexes) => {
      if (isBrandName) {
        const selectedBrand = filterAvailableCombinations(
          flightOffer,
          combinations
        )[newKey];

        return [newKey, ...selectedBrand[0].map((x) => x.Key)];
      }

      const newStreamIndex = [...currentSelectedStreamIndexes];
      newStreamIndex[streamIndex] = newKey;

      return newStreamIndex;
    });
  }

  const selectedBrandName = selectedStreamParts[0];
  const selectedCombination = flightOffer.FlightCombinations.find(
    (combination) => combination.UniformBrandName === selectedBrandName
  );

  return {
    selectedCombination,
    selectedStreamParts,
    changeSelectedStreamParts,
  };
}
